/**
 * Повтор розыгрыша: график, состояние участников и хронология восстанавливаются на любой момент
 * записи. Можно идти по шагам, перематывать ползунком или включить воспроизведение.
 * Участник видит историю своего прогноза, администратор — прогнозы всех игроков.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ChartPayload, Direction, GameReplay, ReplayEvent } from '@mt/protocol';
import { t, tm, tp } from '../../i18n';
import { api, useLoad } from '../../lib/api';
import { ACTION_TITLES, clock, cr, crn, pts } from '../../lib/format';
import { Link } from '../../lib/router';
import { downloadPackage, fetchPackage } from '../../lib/verification';
import { client, useApp } from '../../net/client';
import type { SignedPackage } from '@mt/verification';
import { Chart } from '../Chart';
import { VerifyDialog } from '../VerifyDialog';
import { Avatar, dateTime, signedCr, toneOf } from '../kit';

/** Текст записи хронологии на текущем языке: ходы строятся из полей записи, остальное — по коду сообщения сервера. */
function eventText(e: ReplayEvent): string {
  if (e.kind === 'action' && e.action) {
    // для райза записана итоговая ставка в круге — «райз до», как в тексте сервера
    const title = ACTION_TITLES[e.action === 'raise' ? 'raiseTo' : e.action];
    return `${e.name ?? ''}: ${title}${e.amount ? ' ' + cr(e.amount) : ''}${e.auto ? ' · ' + t('common.auto') : ''}`;
  }
  return tm(e.msg, e.text);
}

interface SeatState {
  committed: number;
  roundBet: number;
  folded: boolean;
  last: string | null;
}

/** Состояние участников после первых n событий хронологии. */
function seatsAt(r: GameReplay, n: number): Map<string, SeatState> {
  const m = new Map(r.seats.map((s) => [s.playerId, { committed: 0, roundBet: 0, folded: false, last: null as string | null }]));
  let round = 0;
  for (const e of r.events.slice(0, n)) {
    if (e.round !== round && e.kind !== 'roundClosed') {
      round = e.round;
      for (const s of m.values()) s.roundBet = 0;
    }
    const s = e.playerId ? m.get(e.playerId) : undefined;
    if (!s) continue;
    if (e.kind === 'action') {
      const a = e.action ?? '';
      if (a === 'ante' || a === 'call' || a === 'allin') {
        s.committed += e.amount ?? 0;
        if (a !== 'ante') s.roundBet += e.amount ?? 0;
      } else if (a === 'bet' || a === 'raise') {
        // для ставки и райза записана итоговая ставка в круге — доплата = разница
        const pay = (e.amount ?? 0) - s.roundBet;
        s.committed += pay;
        s.roundBet = e.amount ?? 0;
      } else if (a === 'fold') s.folded = true;
      s.last = `${ACTION_TITLES[a] ?? a}${e.amount && a !== 'fold' && a !== 'check' ? ' ' + crn(e.amount) : ''}${e.auto ? ' · ' + t('common.auto') : ''}`;
    } else if (e.kind === 'uncalledReturn') {
      s.committed -= e.amount ?? 0;
      s.roundBet -= e.amount ?? 0;
      s.last = t('profile.replay.returned', { amount: crn(e.amount ?? 0) });
    } else if (e.kind === 'autoFold') {
      s.folded = true;
    }
  }
  return m;
}

function forecastAt(r: GameReplay, playerId: string, at: number): { direction: Direction | null; points: number | null } {
  const list = r.forecasts[playerId] ?? [];
  let cur: { direction: Direction | null; points: number | null } = { direction: null, points: null };
  for (const f of list) if (f.at <= at) cur = { direction: f.direction, points: f.points };
  return cur;
}

export function ReplayPage({ gameId, admin }: { gameId: string; admin: boolean }) {
  const { data: r, error } = useLoad(() => api.replay(gameId), [gameId]);
  if (error) return <div className="page"><p className="notice danger">{error}</p></div>;
  if (!r) return <div className="page center dim">{t('profile.replay.loading')}</div>;
  return <Replay r={r} admin={admin} />;
}

function Replay({ r, admin }: { r: GameReplay; admin: boolean }) {
  const me = useApp((s) => s.profile?.id ?? null);
  const total = r.events.length;
  const [pos, setPos] = useState(total);
  const [playing, setPlaying] = useState(false);
  const [verify, setVerify] = useState<SignedPackage | undefined>(undefined);
  const atEnd = pos >= total;

  useEffect(() => {
    if (!playing) return;
    if (atEnd) return setPlaying(false);
    const id = setTimeout(() => setPos((p) => Math.min(total, p + 1)), 650);
    return () => clearTimeout(id);
  }, [playing, pos, atEnd, total]);

  const cursorAt = atEnd ? r.settlement.settledAt : (r.events[Math.max(0, pos - 1)]?.at ?? r.startAt);
  const target = r.startAt + 3 * r.minuteMs;
  const chart: ChartPayload = useMemo(
    () => ({
      gameId: r.gameId,
      units: 'points',
      p0: r.p0,
      p0Points: r.p0,
      tick: r.tick,
      bars: r.bars.filter((b) => b.t < r.startAt || (b.t < target && b.t + r.minuteMs <= cursorAt) || (atEnd && b.t <= target)),
    }),
    [r, cursorAt, atEnd, target],
  );
  const seats = useMemo(() => seatsAt(r, pos), [r, pos]);
  const viewer = admin ? null : me;
  const myForecast = viewer ? forecastAt(r, viewer, cursorAt) : { direction: null, points: null };
  const stage = [...r.events.slice(0, pos)].reverse().find((e) => e.kind === 'stage' || e.kind === 'reveal');
  // открытый инструмент («Открытые карты») известен с начала; состав тройки — с круга 3
  const compositionOpen = atEnd || !!r.instrument || r.events.slice(0, pos).some((e) => e.kind === 'reveal' && e.round === 3);
  const s = r.settlement;
  const participant = !admin && !!me && r.seats.some((x) => x.playerId === me);
  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (e) {
      client.toast('error', (e as Error).message);
    }
  };

  return (
    <div className="page replay-page">
      <nav className="crumbs">
        {admin ? <Link to="/admin?tab=games">{t('profile.replay.crumbAdmin')}</Link> : <Link to="/profile">{t('profile.replay.crumbProfile')}</Link>} <span>/</span> <b>{t('profile.replay.crumbGame', { id: r.gameId })}</b>
      </nav>
      <header className="replay-head panel">
        <div>
          <h1>
            {t('profile.replay.title', { ante: r.ante, date: dateTime(s.settledAt) })}
          </h1>
          <p className="muted-line">
            {r.mode === 'replay' ? t('profile.replay.modeReplay') : t('profile.replay.modeLive')} · {r.source === 'alpaca' ? 'Alpaca' : t('profile.replay.sourceSynthetic')} · {tp('profile.replay.seats', r.seats.length)}
          </p>
        </div>
        <dl className="head-stats">
          <div>
            <dt>{t('profile.replay.result')}</dt>
            <dd>{s.status === 'cancelled' ? t('profile.replay.resultCancelled') : s.kind === 'walkover' ? t('profile.replay.resultWalkover') : t('profile.replay.resultSettled')}</dd>
          </div>
          <div>
            <dt>Δ</dt>
            <dd className={`mono ${s.delta ? toneOf(s.delta) : ''}`}>{pts(s.delta, true)}</dd>
          </div>
          <div>
            <dt>{t('profile.replay.pot')}</dt>
            <dd className="mono">{cr(s.players.reduce((a, p) => a + p.contributed, 0))}</dd>
          </div>
        </dl>
      </header>

      {r.legacy ? (
        <p className="notice warn">{t('profile.replay.legacy')}</p>
      ) : (
        <div className="replay-grid">
          <div className="replay-main">
            <div className="replay-chart">
              <Chart chart={chart} startAt={r.startAt} minuteMs={r.minuteMs} forecast={myForecast} settled={atEnd} />
            </div>
            <div className="panel player-bar">
              <button className="btn tiny" onClick={() => setPos(0)} disabled={pos === 0} aria-label={t('profile.replay.toStart')}>
                ⏮
              </button>
              <button className="btn tiny" onClick={() => setPos((p) => Math.max(0, p - 1))} disabled={pos === 0} aria-label={t('profile.replay.stepBack')}>
                ◀
              </button>
              <button
                className="btn tiny primary"
                onClick={() => {
                  if (atEnd) setPos(0);
                  setPlaying((v) => !v);
                }}
              >
                {playing ? t('profile.replay.pause') : t('profile.replay.play')}
              </button>
              <button className="btn tiny" onClick={() => setPos((p) => Math.min(total, p + 1))} disabled={atEnd} aria-label={t('profile.replay.stepForward')}>
                ▶
              </button>
              <button className="btn tiny" onClick={() => setPos(total)} disabled={atEnd} aria-label={t('profile.replay.toEnd')}>
                ⏭
              </button>
              <input type="range" min={0} max={total} value={pos} onChange={(e) => (setPlaying(false), setPos(Number(e.target.value)))} aria-label={t('profile.replay.position')} />
              <span className="mono small dim">
                {pos}/{total} · {clock(cursorAt)}
              </span>
            </div>
            <p className="replay-stage">{atEnd ? t('profile.replay.stageSettled') : stage ? eventText(stage) : t('profile.replay.stagePrep')}</p>
          </div>

          <aside className="replay-side">
            <section className="panel">
              <h3 className="panel-title">{t('profile.replay.participants')}</h3>
              <ul className="replay-seats">
                {r.seats.map((x) => {
                  const st = seats.get(x.playerId)!;
                  const f = admin || x.playerId === me ? forecastAt(r, x.playerId, cursorAt) : null;
                  const res = atEnd ? s.players.find((p) => p.playerId === x.playerId) : undefined;
                  return (
                    <li key={x.playerId} className={`${st.folded ? 'folded' : ''} ${x.playerId === me ? 'me' : ''}`}>
                      <Avatar id={x.playerId} name={x.name} size={28} bot={x.isBot} />
                      <span className="rs-name">
                        <b>
                          {admin ? <Link to={`/admin/users/${x.playerId}`}>{x.name}</Link> : x.name}
                          {x.playerId === me && <em className="tag me">{t('profile.replay.you')}</em>}
                        </b>
                        <small>{st.folded ? ACTION_TITLES.fold : (st.last ?? '—')}</small>
                      </span>
                      <span className="rs-forecast">
                        {f?.direction ? (
                          <span className={f.direction === 'up' ? 'up' : 'down'}>
                            {f.direction === 'up' ? '▲' : '▼'} {pts(f.points)}
                          </span>
                        ) : f ? (
                          <span className="dim">—</span>
                        ) : null}
                      </span>
                      <span className="rs-money mono">{res ? <b className={toneOf(res.net)}>{signedCr(res.net)}</b> : crn(st.committed)}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="muted-line small">{atEnd ? t('profile.replay.noteNet') : t('profile.replay.noteContributed')} {admin ? t('profile.replay.noteAdmin') : t('profile.replay.notePlayer')}</p>
            </section>
            <section className="panel">
              <h3 className="panel-title">{t('profile.replay.composition')}</h3>
              {compositionOpen ? (
                <div className="composition">
                  {s.composition.map((c) => (
                    <span key={c.symbol} className="ticker">
                      <b>{c.symbol}</b> {c.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="dim">{t('profile.replay.compositionHidden')}</p>
              )}
            </section>
          </aside>
        </div>
      )}

      <div className="replay-bottom">
        {!r.legacy && (
          <section className="panel">
            <h3 className="panel-title">{t('profile.replay.timeline')}</h3>
            <ol className="timeline">
              {r.events.map((e, i) => (
                <TimelineItem key={e.seq} e={e} active={i === pos - 1} future={i >= pos} onClick={() => (setPlaying(false), setPos(i + 1))} />
              ))}
            </ol>
          </section>
        )}
        <section className="panel">
          <h3 className="panel-title">{t('profile.replay.settlement')}</h3>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('profile.col.player')}</th>
                  <th>{t('profile.col.forecast')}</th>
                  <th className="num">{t('profile.col.error')}</th>
                  <th className="num">{t('profile.col.contributed')}</th>
                  <th className="num">{t('profile.col.payout')}</th>
                  <th className="num">{t('profile.col.net')}</th>
                </tr>
              </thead>
              <tbody>
                {s.players.map((p) => (
                  <tr key={p.playerId} className={p.playerId === me ? 'me' : ''}>
                    <td data-label={t('profile.col.player')}>{p.name}</td>
                    <td data-label={t('profile.col.forecast')}>{p.folded ? <span className="dim">{ACTION_TITLES.fold}</span> : p.direction ? <span className={p.direction === 'up' ? 'up' : 'down'}>{p.direction === 'up' ? '▲' : '▼'} {pts(p.points)}</span> : '—'}</td>
                    <td data-label={t('profile.col.error')} className="num mono">
                      {p.error === null ? '—' : pts(p.error)}
                    </td>
                    <td data-label={t('profile.col.contributed')} className="num mono">
                      {crn(p.contributed)}
                    </td>
                    <td data-label={t('profile.col.payout')} className="num mono">
                      {crn(p.payout + p.refund)}
                    </td>
                    <td data-label={t('profile.col.net')} className={`num mono ${toneOf(p.net)}`}>
                      {signedCr(p.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted-line small">
            {t('profile.replay.summary', { p0: pts(s.p0), p1: pts(s.p1), commission: s.commissionTotal })}
            {s.reason || s.reasonMsg ? ` ${tm(s.reasonMsg, s.reason ?? '')}.` : ''}
          </p>
          {participant && !r.legacy && (
            <div className="row-actions">
              <button className="btn tiny" onClick={() => void run(async () => setVerify((await fetchPackage(r.gameId)).signed))}>
                {t('profile.replay.verify')}
              </button>
              <button className="btn tiny" onClick={() => void run(() => downloadPackage(r.gameId))}>
                {t('profile.replay.download')}
              </button>
            </div>
          )}
        </section>
      </div>
      {verify && <VerifyDialog initial={verify} onClose={() => setVerify(undefined)} />}
    </div>
  );
}

function TimelineItem({ e, active, future, onClick }: { e: ReplayEvent; active: boolean; future: boolean; onClick: () => void }) {
  return (
    <li className={`tl-item ${e.kind} ${active ? 'active' : ''} ${future ? 'future' : ''}`}>
      <button type="button" onClick={onClick}>
        <span className="mono dim">{clock(e.at)}</span>
        <span className="tl-round">{e.round === 0 ? t('profile.replay.roundPrep') : t('profile.replay.round', { n: e.round })}</span>
        <span className="tl-text">{eventText(e)}</span>
      </button>
    </li>
  );
}
